import React, { useEffect, useState } from 'react';
import { Cookie, X, ChartBar } from '@phosphor-icons/react';
import { hasDecided, writeConsent, LEGACY_KEY } from '../utils/consent';

const CookieConsent = () => {
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [marketing, setMarketing] = useState(false);

  useEffect(() => {
    try {
      // Re-prompt if user only ever saw the v1 banner (no marketing choice).
      const legacyOnly = !!localStorage.getItem(LEGACY_KEY) && !hasDecided();
      if (!hasDecided() || legacyOnly) {
        const t = setTimeout(() => setVisible(true), 800);
        return () => clearTimeout(t);
      }
    } catch { /* localStorage blocked → stay silent, no tracking by default */ }
  }, []);

  const finish = (config) => {
    writeConsent(config);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed bottom-2 left-2 right-2 sm:left-auto sm:right-6 sm:bottom-6 sm:max-w-md z-[90] bg-white border border-[#D6D3D1] rounded-2xl shadow-2xl p-4 sm:p-5 animate-in slide-in-from-bottom-4 duration-300"
      role="dialog"
      aria-labelledby="cookie-consent-title"
      data-testid="cookie-consent-banner"
    >
      <div className="flex items-start gap-3">
        <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-[#AB1C24]/10 flex items-center justify-center flex-shrink-0">
          <Cookie size={20} className="text-[#AB1C24]" weight="duotone" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 id="cookie-consent-title" className="font-heading font-semibold text-[#292524] text-sm mb-1">
            Cookies & Tracking
          </h3>
          <p className="text-[11px] sm:text-xs text-[#57534E] leading-relaxed mb-3">
            <strong>Technisch notwendige Cookies</strong> (Login, Spracheinstellungen) sind immer aktiv.
            Marketing-Cookies (Meta Pixel) helfen uns, Habla zu verbessern — du entscheidest.{' '}
            <a href="/#legal" className="text-[#AB1C24] underline hover:text-[#8A151C]">Details</a>
          </p>

          {showDetails && (
            <div className="space-y-2 mb-3 bg-stone-50 rounded-lg p-3 border border-stone-200" data-testid="cookie-details">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#292524] flex items-center gap-1">
                    <Cookie size={12} weight="duotone" className="text-emerald-600" />
                    Notwendig
                  </p>
                  <p className="text-[10px] text-[#78716C] leading-snug">Login-Session, Sprache. Ohne diese funktioniert die Plattform nicht.</p>
                </div>
                <span className="text-[10px] font-bold text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5 flex-shrink-0">Immer an</span>
              </div>
              <div className="flex items-start justify-between gap-3 pt-2 border-t border-stone-200">
                <div className="min-w-0">
                  <p className="text-xs font-semibold text-[#292524] flex items-center gap-1">
                    <ChartBar size={12} weight="duotone" className="text-[#AB1C24]" />
                    Marketing &amp; Tracking
                  </p>
                  <p className="text-[10px] text-[#78716C] leading-snug">Meta Pixel — misst Anmeldungen &amp; Käufe für unsere Werbung.</p>
                </div>
                <button
                  type="button"
                  role="switch"
                  aria-checked={marketing}
                  onClick={() => setMarketing(v => !v)}
                  className={`relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors ${marketing ? 'bg-[#AB1C24]' : 'bg-stone-300'}`}
                  data-testid="cookie-marketing-toggle"
                >
                  <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transform transition ${marketing ? 'translate-x-4' : 'translate-x-0.5'} translate-y-0.5`} />
                </button>
              </div>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2">
            <button
              onClick={() => finish({ marketing: true })}
              className="flex-1 min-w-[120px] bg-[#AB1C24] hover:bg-[#8A151C] text-white text-xs sm:text-sm font-medium px-3 py-2 rounded-lg transition-colors"
              data-testid="cookie-accept-all-btn"
            >
              Alle akzeptieren
            </button>
            <button
              onClick={() => finish({ marketing: false })}
              className="flex-1 min-w-[120px] bg-white border border-[#D6D3D1] hover:border-[#AB1C24] hover:text-[#AB1C24] text-[#57534E] text-xs sm:text-sm font-medium px-3 py-2 rounded-lg transition-colors"
              data-testid="cookie-necessary-btn"
            >
              Nur notwendige
            </button>
            {!showDetails ? (
              <button
                onClick={() => setShowDetails(true)}
                className="text-[11px] text-[#78716C] underline hover:text-[#292524] w-full text-left"
                data-testid="cookie-customize-btn"
              >
                Anpassen
              </button>
            ) : (
              <button
                onClick={() => finish({ marketing })}
                className="w-full bg-[#292524] hover:bg-[#1c1917] text-white text-xs sm:text-sm font-medium px-3 py-2 rounded-lg transition-colors"
                data-testid="cookie-save-custom-btn"
              >
                Auswahl speichern
              </button>
            )}
          </div>
        </div>
        <button
          onClick={() => finish({ marketing: false })}
          className="p-1 text-[#A8A29E] hover:text-[#292524] transition-colors flex-shrink-0"
          aria-label="Schließen"
          data-testid="cookie-dismiss-btn"
        >
          <X size={18} />
        </button>
      </div>
    </div>
  );
};

export default CookieConsent;
